/**
 * Incident ↔ media-signal corroboration (v1, #183) — the pure math behind the
 * "N social reports nearby" chip on an incident's detail panel.
 *
 * "Nearby" = a bbox around the incident centroid; "recent" = the wire's
 * default 72 h freshness window. The count comes from the existing
 * GET /api/media-signals list (bbox restricts to geo-bearing signals — honest
 * geo only, so the count never includes a guessed location). The chip renders
 * ONLY when N > 0 (PART IX contextual-only rule, the #185 worked example).
 */

export const CORROBORATION_RADIUS_KM = 50;
export const CORROBORATION_WINDOW_HOURS = 72;

export interface CorroborationBbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

/**
 * A lat/lon bbox spanning ±radiusKm around a point. Longitude widens with
 * latitude (1° of longitude shrinks toward the poles); both axes clamp to
 * valid ranges so a near-pole incident still yields a legal bbox.
 */
export function corroborationBbox(
  lon: number,
  lat: number,
  radiusKm: number = CORROBORATION_RADIUS_KM,
): CorroborationBbox {
  const dLat = radiusKm / 110.574;
  // cos clamps away from 0 so a polar point doesn't divide by ~0 into a
  // whole-world span.
  const cosLat = Math.max(0.05, Math.cos((lat * Math.PI) / 180));
  const dLon = radiusKm / (111.32 * cosLat);
  return {
    west: Math.max(-180, lon - dLon),
    south: Math.max(-90, lat - dLat),
    east: Math.min(180, lon + dLon),
    north: Math.min(90, lat + dLat),
  };
}

/** Chip copy — null when there is nothing to corroborate (the chip's
 *  render-only-when-content gate lives in this return). */
export function corroborationLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 social report nearby' : `${count} social reports nearby`;
}
